// TEAM-KPI (founder-approved 2026-10-21; spec 13 §Team KPIs): a team's scorecard for a week, one
// self-contained card for every place a team is shown — the team page (Setup › Teams drawer),
// the chart scoped to a team (the sidebar's team view), Cadence's team scorecards. The header is
// the team's band and KPI score; then one dense row per team KPI: its name, where its value
// comes from (the team's own, or its member seats' summed / averaged), this week's value against
// its target, and its health under the metric's own bands (the company's otherwise) — a dot and
// a word, never colour alone. The team's lead and company managers (`can_edit`) add, edit and
// remove KPIs with the shared KPI assignment editor in team mode; members read.
// On load: the team's scorecard and its KPI set (two `kpi` reads; mount behind
// `useFeature("kpi")`).
import { TEAM_ROLLUP_LABELS } from "@companyos/domain-kpi";
import {
  BandBadge,
  Button,
  cn,
  EmptyState,
  LensBandDot,
  type LensBandTone,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { ScorecardMetric } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatMetricValue, formatScore, formatTarget } from "../format";
import { companyQuery } from "../queries";
import { Loading } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { ErrorNotice, FormError } from "../structure/parts";
import { HEALTH_LABEL, HEALTH_TEXT, healthTone, lineHealth } from "./health";
import { KpiAssignDialog } from "./kpi-assign-dialog";
import {
  type TeamMetricLine,
  type TeamScore,
  teamKpiKeys,
  teamMetricsQuery,
  teamScorecardQuery,
} from "./team-queries";

/** One team KPI's health: the dot and its word, in the health's text colour. */
export function HealthCell({ m }: { m: ScorecardMetric }) {
  const h = lineHealth(m);
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${HEALTH_TEXT[h ?? "none"]}`}
      data-health={h ?? "none"}
    >
      <LensBandDot band={healthTone(h)} />
      {HEALTH_LABEL[h ?? "none"]}
    </span>
  );
}

export function TeamScorecardCard({
  api,
  company,
  team,
  week,
  compact = false,
}: {
  api: Api;
  company: CompanyAccess;
  team: { id: string; name: string };
  week: string;
  /** The chart's strip: no caption line, no empty-state card. */
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const co = company.id;
  const card = useQuery(teamScorecardQuery(api, co, team.id, week));
  const set = useQuery(teamMetricsQuery(api, co, team.id));
  const detail = useQuery(companyQuery(api, co));
  const currency = detail.data?.reporting_currency ?? "USD";
  const canEdit = set.data?.can_edit ?? false;
  const [dialog, setDialog] = useState<"add" | { line: TeamMetricLine } | null>(null);
  const remove = useMutation({
    mutationFn: (metricId: string) =>
      api.client.teams.removeMetric({ params: { co, id: team.id, metric_id: metricId } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["company", co] });
      void qc.invalidateQueries({ queryKey: teamKpiKeys.scores(co, week) });
    },
  });
  const lines = new Map((set.data?.data ?? []).map((l) => [l.metric_def_id, l]));
  const rows = card.data?.metrics ?? [];

  return (
    <section
      className="flex min-w-0 flex-col gap-2"
      aria-label={`${team.name}: team scorecard`}
      data-slot="team-scorecard"
      data-team={team.id}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {card.data ? <BandBadge band={card.data.band} /> : null}
          <span className="text-sm font-medium tabular-nums">
            KPI score {card.data ? formatScore(card.data.kpi_score) : "…"}
          </span>
          {compact ? null : (
            <span className="text-xs text-muted-foreground">
              {rows.length} team KPI{rows.length === 1 ? "" : "s"} · week {week}
            </span>
          )}
        </div>
        {canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setDialog("add")}
            data-slot="add-team-kpi"
          >
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            Add a KPI
          </Button>
        ) : null}
      </div>

      {card.isPending ? (
        <Loading label="Loading the team scorecard" rows={3} />
      ) : card.isError ? (
        <ErrorNotice what="The team scorecard" error={card.error} />
      ) : rows.length === 0 ? (
        compact ? (
          <p className="text-xs text-muted-foreground">No team KPIs yet.</p>
        ) : (
          <EmptyState
            variant="card"
            title="No team KPIs yet"
            description={
              canEdit
                ? "Add a KPI to the team: its own number, or its members' seats summed or averaged."
                : "The team's lead or a manager adds its KPIs."
            }
          />
        )
      ) : (
        <Table className={DENSE_TABLE} data-slot="team-kpi-table">
          <TableCaption className="sr-only">
            The team's KPIs: where each value comes from, this week's value, target and health.
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">KPI</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Value comes from
              </TableHead>
              <TableHead scope="col" className="text-right">
                Value
              </TableHead>
              <TableHead scope="col" className="text-right">
                Target
              </TableHead>
              <TableHead scope="col">Health</TableHead>
              {canEdit ? (
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((m) => {
              const line = lines.get(m.metric_def_id);
              return (
                <TableRow key={m.metric_def_id} className="group/row" data-kpi={m.key}>
                  <TableHead scope="row" className="max-w-64 truncate font-medium">
                    {m.name}
                  </TableHead>
                  <TableCell className="text-muted-foreground max-md:hidden" data-field="rollup">
                    {TEAM_ROLLUP_LABELS[m.rollup ?? "none"]}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${HEALTH_TEXT[lineHealth(m) ?? "none"]}`}
                    data-field="actual"
                  >
                    {formatMetricValue(m.actual, m.unit, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-field="target">
                    {formatTarget(m, currency)}
                  </TableCell>
                  <TableCell data-field="health">
                    <HealthCell m={m} />
                  </TableCell>
                  {canEdit ? (
                    <TableCell className="w-px">
                      <RowActions>
                        {line ? (
                          <RowAction
                            icon={PencilIcon}
                            label={`Edit ${m.name}`}
                            tip="Edit roll-up, target, weight and bands"
                            onClick={() => setDialog({ line })}
                          />
                        ) : null}
                        <RowAction
                          icon={Trash2Icon}
                          label={`Remove ${m.name} from ${team.name}`}
                          tip="Remove from the team (its history stays)"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(m.metric_def_id)}
                        />
                      </RowActions>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <FormError what="Removing the KPI" error={remove.error} />

      {dialog ? (
        <KpiAssignDialog
          api={api}
          company={company}
          team={team}
          onSeat={[]}
          {...(dialog === "add" ? {} : { editingTeam: { line: dialog.line } })}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}

const BAND_TONE: Record<TeamScore["band"], LensBandTone> = {
  great: "green",
  regular: "green",
  average: "amber",
  low: "red",
  insufficient_data: "none",
};

/** A team's small score chip (the chart's Teams enclosure and panel): band dot and KPI score. */
export function TeamScoreChip({ score, className }: { score: TeamScore; className?: string }) {
  return (
    <span
      data-slot="team-score-chip"
      data-band={score.band}
      title={`Team score: ${formatScore(score.kpi_score)} · ${healthCounts(score)}`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-sm border border-border bg-card px-1.5 text-[11px] leading-4 font-medium text-secondary tabular-nums",
        className,
      )}
    >
      <LensBandDot band={BAND_TONE[score.band]} />
      {score.kpi_score === null ? "—" : Math.round(score.kpi_score)}
    </span>
  );
}

/** "3 KPIs · 1 green · 1 amber · 1 red". */
export function healthCounts(s: TeamScore): string {
  return `${s.metrics} KPI${s.metrics === 1 ? "" : "s"} · ${s.health.green} green · ${s.health.amber} amber · ${s.health.red} red`;
}

/** The band tone of a team's score (the chip's and the list's dot). */
export const teamBandTone = (s: TeamScore): LensBandTone => BAND_TONE[s.band];
